import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { accountRequest } from "@/accountApi";

export default function ForgotPassword() {
    const [sent, setSent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        const data = new FormData(e.currentTarget);
        try {
            await accountRequest("/api/account/forgot-password", {
                method: "POST",
                body: { email: data.get("email") },
            });
            setSent(true);
            toast.success("Reset link requested");
        } catch (error) {
            toast.error("Could not request reset", {
                description:
                    error instanceof Error
                        ? error.message
                        : "Please try again or contact support.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Password reset"
            title="Reset access securely."
            subtitle="Enter your account email. If it exists, we will email a time-limited reset link."
            sideTitle="Forgot password links expire quickly."
            sideBody="For security, the form always returns the same confirmation message and never confirms whether an email address is registered."
            sideMeta={[
                { k: "30m", v: "expiry" },
                { k: "Email", v: "reset link" },
                { k: "Secure", v: "token" },
            ]}
        >
            {sent ? (
                <div className="border border-ink p-6 bg-surface">
                    <h2 className="font-display text-2xl font-bold text-ink">Check your inbox.</h2>
                    <p className="text-ink-soft mt-3">
                        If an account exists for that email address, a reset link has been sent.
                    </p>
                    <Link to="/login" className="btn btn-primary mt-6 h-11 px-4 text-xs">
                        Back to sign in <ArrowRight size={12} />
                    </Link>
                </div>
            ) : (
                <form data-testid="forgot-form" onSubmit={submit} className="space-y-5">
                    <div>
                        <div className="flex items-end justify-between mb-2">
                            <label className="text-sm font-medium text-ink">Account email</label>
                            <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">01</span>
                        </div>
                        <input
                            data-testid="forgot-input-email"
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            className="input-sharp"
                            placeholder="jane@company.com"
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="forgot-submit"
                        disabled={submitting}
                        className="btn btn-primary w-full h-12 disabled:opacity-70"
                    >
                        {submitting ? "Sending..." : "Send reset link"}
                        <ArrowRight size={14} />
                    </button>
                    <Link to="/login" className="inline-flex text-sm text-ink link-underline">
                        Back to sign in
                    </Link>
                </form>
            )}
        </AuthShell>
    );
}
